/**
 * Student leak detail (/portal/leaks/:id). Read-only summary + status + images
 * (both initial and resolution images visible) + optional resolution comment.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, LinkSimple, ImageSquare } from "@phosphor-icons/react";
import { leaksApi } from "../../api/leaks";
import LeakStatusBadge from "../../components/leaks/LeakStatusBadge";

export default function PortalLeakDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [leak, setLeak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setLeak(await leaksApi.get(id));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (error || !leak) {
    return <p className="text-sm text-red-600">{t("leaks.not_found")}</p>;
  }

  const initialImgs = leak.images.filter((i) => i.kind === "initial");
  const resolutionImgs = leak.images.filter((i) => i.kind === "resolution");

  return (
    <div data-testid="portal-leak-detail" className="space-y-8 max-w-3xl">
      <Link
        to="/portal/leaks"
        data-testid="portal-leak-back"
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} weight="bold" />
        {t("portal.nav.leaks")}
      </Link>

      <header className="space-y-3">
        <LeakStatusBadge status={leak.status} />
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          {leak.title}
        </h1>
        <a
          href={leak.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 break-all"
        >
          <LinkSimple size={14} />
          {leak.url}
        </a>
      </header>

      {leak.description && (
        <section className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">
            {leak.description}
          </p>
        </section>
      )}

      {initialImgs.length > 0 && (
        <section data-testid="portal-leak-initial-images">
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">
            {t("leaks.attached_images")}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {initialImgs.map((img) => (
              <a
                key={img.id}
                href={leaksApi.imageUrl(img.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="block aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50"
              >
                <img
                  src={leaksApi.imageUrl(img.id)}
                  alt={img.original_filename}
                  className="h-full w-full object-cover"
                />
              </a>
            ))}
          </div>
        </section>
      )}

      {(leak.resolution_comment || resolutionImgs.length > 0) && (
        <section
          data-testid="portal-leak-resolution"
          className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 space-y-4"
        >
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            {t("leaks.agency_note")}
          </h2>
          {leak.resolution_comment && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">
              {leak.resolution_comment}
            </p>
          )}
          {resolutionImgs.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {resolutionImgs.map((img) => (
                <a
                  key={img.id}
                  href={leaksApi.imageUrl(img.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-square rounded-xl overflow-hidden border border-slate-200 bg-white"
                >
                  <img
                    src={leaksApi.imageUrl(img.id)}
                    alt={img.original_filename}
                    className="h-full w-full object-cover"
                  />
                </a>
              ))}
            </div>
          )}
        </section>
      )}

      {initialImgs.length === 0 && (
        <p className="text-xs text-slate-400 inline-flex items-center gap-1.5">
          <ImageSquare size={14} />
          {t("leaks.no_image")}
        </p>
      )}
    </div>
  );
}
